import styled from '@emotion/styled'
import {
  AppstoreAddOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  Badge,
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Upload,
  message,
} from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { FC, useEffect, useMemo, useState } from 'react'
import { allowFileExtensionsImage, backendUrl } from 'config'
import {
  useCreateProductRecipe,
  useUpdateProductRecipe,
} from 'hooks/useProductRecipe'
import { uploadFile } from 'services/factoryAPI'
import type {
  ProductRecipeItemRecord,
  RecipeFormEdit,
  RecipeItemInput,
} from 'services/interface/productRecipe.interface'
import StockImageThumb from '../StockImageThumb'
import RecipeStockPickerModal, {
  type PickedStockItem,
} from './RecipeStockPickerModal'
import {
  type BundleItemKindFilter,
  displayRefTypeLabel,
  MASTER_STOCK_LABEL,
  matchesBundleItemKindFilter,
  NESTED_SET_LABEL,
  SET_TAG_LABEL,
} from './bundleLabels'

type LocalItemRow = {
  key: string
  ref_id: string
  ref_type: 'product_stock' | 'product_recipe'
  ref_label: string
  ref_stock_type?: 'sku' | 'general' | null
  ref_stock_type_label?: string | null
  ref_image_url?: string | null
  ref_description?: string | null
  ref_balance?: number | null
  quantity: number
  remark?: string
}

interface RecipeFormModalProps {
  open: boolean
  onClose: () => void
  editing?: RecipeFormEdit | null
  loading_detail?: boolean
}

const tagColor = (row: LocalItemRow): string => {
  if (
    row.ref_stock_type_label === 'สต็อกทั่วไป' ||
    row.ref_stock_type_label === 'มาสเตอร์สินค้า' ||
    row.ref_stock_type_label === MASTER_STOCK_LABEL
  )
    return 'blue'
  if (row.ref_stock_type_label === 'สต็อก SKU') return 'purple'
  if (row.ref_type === 'product_recipe') return 'magenta'
  return 'default'
}

const extractImageIds = (fileList: UploadFile[]): string[] =>
  fileList
    .map((f) => (f.response?.id as string) ?? (f as UploadFile & { id?: string }).id)
    .filter((id): id is string => !!id)

const buildFileListFromIds = (ids?: string[] | null): UploadFile[] =>
  (ids ?? []).map((id, index) => ({
    uid: id,
    name: `image-${index + 1}`,
    status: 'done' as const,
    id,
    url: `${backendUrl}images/${id}`,
  }))

const mapApiItems = (items: ProductRecipeItemRecord[]): LocalItemRow[] =>
  items.map((item, i) => ({
    key: item.id || `item-${i}`,
    ref_id: item.ref_id,
    ref_type: item.ref_type,
    ref_label: item.ref_label || item.ref_name || item.ref_id,
    ref_stock_type: item.ref_stock_type ?? undefined,
    ref_stock_type_label: item.ref_stock_type_label,
    ref_image_url: item.ref_image_url ?? undefined,
    ref_description: item.ref_description ?? undefined,
    ref_balance: item.ref_balance ?? undefined,
    quantity: Number(item.quantity) || 0,
    remark: item.remark ?? undefined,
  }))

const RecipeFormModal: FC<RecipeFormModalProps> = ({
  open,
  onClose,
  editing,
  loading_detail,
}) => {
  const [form] = Form.useForm<{
    name: string
    description?: string
    is_active: boolean
  }>()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [items, setItems] = useState<LocalItemRow[]>([])
  const [picker_open, set_picker_open] = useState(false)
  const [item_kind_filter, set_item_kind_filter] =
    useState<BundleItemKindFilter>('all')
  const is_edit = !!editing?.id

  const { mutate: createMutate, isPending: creating } = useCreateProductRecipe()
  const { mutate: updateMutate, isPending: updating } = useUpdateProductRecipe()

  const item_count = items.length

  const filtered_items = useMemo(
    () => items.filter((row) => matchesBundleItemKindFilter(row, item_kind_filter)),
    [items, item_kind_filter]
  )

  const master_count = useMemo(
    () => items.filter((row) => matchesBundleItemKindFilter(row, 'general')).length,
    [items]
  )
  const sku_count = useMemo(
    () => items.filter((row) => matchesBundleItemKindFilter(row, 'sku')).length,
    [items]
  )

  useEffect(() => {
    if (!open) {
      set_item_kind_filter('all')
      return
    }
    if (editing) {
      form.setFieldsValue({
        name: editing.name,
        description: editing.description ?? undefined,
        is_active: editing.is_active !== false,
      })
      setFileList(buildFileListFromIds(editing.images))
      setItems(mapApiItems(editing.items ?? []))
    } else {
      form.resetFields()
      form.setFieldsValue({ is_active: true })
      setFileList([])
      setItems([])
    }
  }, [open, editing, form])

  const handlePickStock = (picked: PickedStockItem) => {
    if (items.some((r) => r.ref_id === picked.ref_id)) {
      message.warning('รายการนี้ถูกเพิ่มแล้ว')
      return
    }
    setItems((prev) => [
      ...prev,
      {
        key: `new-${picked.ref_id}-${Date.now()}`,
        ref_id: picked.ref_id,
        ref_type: picked.ref_type,
        ref_label: picked.ref_label,
        ref_stock_type: picked.ref_stock_type,
        ref_stock_type_label: picked.ref_stock_type_label,
        ref_image_url: picked.ref_image_url,
        ref_description: picked.ref_description ?? undefined,
        ref_balance: picked.ref_balance,
        quantity: 1,
      },
    ])
  }

  const removeItem = (key: string) => {
    setItems((prev) => prev.filter((r) => r.key !== key))
  }

  const updateItemQuantity = (key: string, quantity: number) => {
    setItems((prev) =>
      prev.map((r) => (r.key === key ? { ...r, quantity } : r))
    )
  }

  const buildItemsPayload = (): RecipeItemInput[] =>
    items.map((row, index) => ({
      ref_id: row.ref_id,
      ref_type: row.ref_type,
      quantity: row.quantity,
      sequence: index,
      remark: row.remark,
      is_active: true,
    }))

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (items.length === 0) {
        message.warning('กรุณาเพิ่มส่วนประกอบอย่างน้อย 1 รายการ')
        return
      }

      const images = extractImageIds(fileList)
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        images: images.length > 0 ? images : undefined,
        is_active: values.is_active !== false,
        items: buildItemsPayload(),
      }

      if (is_edit && editing) {
        updateMutate(
          { id: editing.id, ...payload },
          {
            onSuccess: () => {
              message.success(`บันทึก${SET_TAG_LABEL}แล้ว`)
              onClose()
            },
            onError: (err) => message.error(err.message || 'บันทึกไม่สำเร็จ'),
          }
        )
        return
      }

      createMutate(payload, {
        onSuccess: () => {
          message.success(`เพิ่ม${SET_TAG_LABEL}แล้ว`)
          onClose()
        },
        onError: (err) => message.error(err.message || 'เพิ่มไม่สำเร็จ'),
      })
    })
  }

  const modalTitle = useMemo(
    () => (
      <ModalTitleWrap>
        <TitleIcon aria-hidden>
          <AppstoreAddOutlined />
        </TitleIcon>
        <div>
          <TitleText>
            {is_edit ? `แก้ไข${SET_TAG_LABEL}` : `สร้าง${SET_TAG_LABEL}`}
          </TitleText>
          <TitleSub>
            กำหนดข้อมูล{SET_TAG_LABEL}และเลือกส่วนประกอบจากสต็อก
          </TitleSub>
        </div>
      </ModalTitleWrap>
    ),
    [is_edit]
  )

  return (
    <>
      <StyledModal
        title={modalTitle}
        open={open}
        onCancel={onClose}
        onOk={handleSubmit}
        okText={is_edit ? `บันทึก${SET_TAG_LABEL}` : `สร้าง${SET_TAG_LABEL}`}
        cancelText="ยกเลิก"
        confirmLoading={creating || updating}
        okButtonProps={{ disabled: !!loading_detail, size: 'large' }}
        cancelButtonProps={{ size: 'large' }}
        destroyOnClose
        centered
        width={960}
        bodyStyle={{ padding: 0, overflow: 'hidden' }}
      >
        {loading_detail ? (
          <SpinWrap>
            <Spin size="large" />
          </SpinWrap>
        ) : (
          <Layout>
            <LeftColumn>
              <SectionLabel>
                <FileTextOutlined /> ข้อมูล{SET_TAG_LABEL}
              </SectionLabel>

              <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item
                  label={`ชื่อ${SET_TAG_LABEL}`}
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: `กรุณาระบุชื่อ${SET_TAG_LABEL}`,
                    },
                  ]}
                >
                  <Input
                    placeholder="เช่น ชุดของขวัญ A"
                    size="large"
                    maxLength={255}
                  />
                </Form.Item>

                <Form.Item label="รายละเอียด" name="description">
                  <Input.TextArea
                    rows={4}
                    placeholder={`หมายเหตุหรือคำอธิบาย${SET_TAG_LABEL}`}
                    style={{ resize: 'vertical' }}
                  />
                </Form.Item>

                <StatusCard>
                  <StatusText>
                    <strong>สถานะการใช้งาน</strong>
                    <span>เปิดใช้งานเมื่อพร้อมนำไปใช้งาน</span>
                  </StatusText>
                  <Form.Item
                    name="is_active"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </StatusCard>

                <Form.Item label="รูปภาพ (สูงสุด 5)" style={{ marginBottom: 0 }}>
                  <UploadCompact>
                    <Upload
                      listType="picture-card"
                      fileList={fileList}
                      accept={allowFileExtensionsImage}
                      maxCount={5}
                      customRequest={async ({ file, onSuccess, onError }) => {
                        try {
                          const result = await uploadFile(file as File)
                          onSuccess?.(result)
                        } catch (e) {
                          onError?.(e as Error)
                        }
                      }}
                      onChange={({ fileList: next }) => {
                        setFileList(
                          next.map((item) =>
                            item.response
                              ? {
                                  ...item,
                                  id: item.response.id,
                                  url: item.response.url,
                                }
                              : item
                          )
                        )
                      }}
                    >
                      {fileList.length >= 5 ? null : (
                        <UploadTrigger>
                          <PlusOutlined />
                          <span>เพิ่มรูป</span>
                        </UploadTrigger>
                      )}
                    </Upload>
                  </UploadCompact>
                </Form.Item>
              </Form>
            </LeftColumn>

            <RightColumn>
              <RightHeader>
                <SectionLabel $light>
                  ส่วนประกอบ
                  <Badge
                    count={item_count}
                    showZero
                    color={item_count > 0 ? '#1677ff' : '#d9d9d9'}
                    style={{ marginLeft: 8 }}
                  />
                </SectionLabel>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => set_picker_open(true)}
                >
                  เพิ่มจากสต็อก
                </Button>
              </RightHeader>

              {items.length > 0 && (
                <ItemsFilterBar>
                  <ItemKindSegmented
                    value={item_kind_filter}
                    onChange={(v) =>
                      set_item_kind_filter(v as BundleItemKindFilter)
                    }
                    options={[
                      { label: `ทั้งหมด (${item_count})`, value: 'all' },
                      {
                        label: `${MASTER_STOCK_LABEL} (${master_count})`,
                        value: 'general',
                      },
                      { label: `สต็อก SKU (${sku_count})`, value: 'sku' },
                    ]}
                  />
                </ItemsFilterBar>
              )}

              <ItemsScroll>
                {items.length === 0 ? (
                  <EmptyWrap>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <EmptyDesc>
                          <strong>ยังไม่มีส่วนประกอบ</strong>
                          <span>
                            เลือกจาก{MASTER_STOCK_LABEL} สต็อก SKU หรือ{NESTED_SET_LABEL}
                          </span>
                        </EmptyDesc>
                      }
                    >
                      <Button
                        type="primary"
                        ghost
                        icon={<PlusOutlined />}
                        onClick={() => set_picker_open(true)}
                      >
                        เลือกส่วนประกอบ
                      </Button>
                    </Empty>
                  </EmptyWrap>
                ) : filtered_items.length === 0 ? (
                  <EmptyWrap>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="ไม่มีรายการตามตัวกรอง"
                    />
                  </EmptyWrap>
                ) : (
                  filtered_items.map((row, index) => (
                    <ItemCard key={row.key}>
                      <ItemIndex>{index + 1}</ItemIndex>
                      <ItemThumb>
                        <StockImageThumb src={row.ref_image_url} alt={row.ref_label} />
                      </ItemThumb>
                      <ItemBody>
                        <ItemTop>
                          <ItemTitle title={row.ref_label}>{row.ref_label}</ItemTitle>
                          <Tag color={tagColor(row)} style={{ margin: 0, flexShrink: 0 }}>
                            {displayRefTypeLabel(
                              row.ref_stock_type_label,
                              row.ref_type
                            )}
                          </Tag>
                        </ItemTop>
                        {(row.ref_description || row.ref_balance != null) && (
                          <ItemMeta>
                            {row.ref_description && (
                              <span>{row.ref_description}</span>
                            )}
                            {row.ref_balance != null && (
                              <span>
                                คงเหลือ {row.ref_balance.toLocaleString()}
                              </span>
                            )}
                          </ItemMeta>
                        )}
                        <QtyRow>
                          <QtyLabel>จำนวนต่อ{SET_TAG_LABEL}</QtyLabel>
                          <InputNumber
                            min={0}
                            value={row.quantity}
                            size="middle"
                            style={{ width: 120 }}
                            onChange={(v) =>
                              updateItemQuantity(row.key, Number(v) || 0)
                            }
                          />
                        </QtyRow>
                      </ItemBody>
                      <Tooltip title="ลบรายการ">
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeItem(row.key)}
                        />
                      </Tooltip>
                    </ItemCard>
                  ))
                )}
              </ItemsScroll>
            </RightColumn>
          </Layout>
        )}
      </StyledModal>

      <RecipeStockPickerModal
        open={picker_open}
        onClose={() => set_picker_open(false)}
        onPick={handlePickStock}
        exclude_ref_ids={items.map((i) => i.ref_id)}
        exclude_recipe_id={editing?.id ?? null}
      />
    </>
  )
}

export default RecipeFormModal

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-modal-footer {
    padding: 12px 20px;
  }
`

const ModalTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const TitleIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1677ff 0%, #69b1ff 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`

const TitleText = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: #141414;
`

const TitleSub = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #8c8c8c;
  margin-top: 2px;
`

const Layout = styled.div`
  display: flex;
  min-height: 420px;
  max-height: min(68vh, 560px);

  @media (max-width: 768px) {
    flex-direction: column;
    max-height: none;
  }
`

const LeftColumn = styled.div`
  flex: 0 0 340px;
  padding: 20px 22px;
  border-right: 1px solid #f0f0f0;
  overflow-y: auto;
  background: #fff;

  @media (max-width: 768px) {
    flex: none;
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
  }
`

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #f8fafc 0%, #f5f5f5 100%);
`

const SectionLabel = styled.div<{ $light?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$light ? '#434343' : '#141414')};
  margin-bottom: 14px;
  letter-spacing: 0.02em;
`

const StatusCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
`

const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  color: #595959;

  strong {
    font-size: 13px;
    color: #141414;
  }
`

const UploadCompact = styled.div`
  .ant-upload-list.ant-upload-list-picture-card {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .ant-upload.ant-upload-select {
    width: 88px !important;
    height: 88px !important;
    margin: 0 !important;
  }
  .ant-upload-list-item-container {
    width: 88px !important;
    height: 88px !important;
    margin: 0 !important;
  }
`

const UploadTrigger = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #1677ff;
  font-size: 12px;
`

const RightHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 0;
  flex-shrink: 0;
`

const ItemsFilterBar = styled.div`
  padding: 0 14px 8px;
  flex-shrink: 0;
`

const ItemKindSegmented = styled(Segmented)`
  &&.ant-segmented {
    width: fit-content;
    max-width: 100%;
  }
  && .ant-segmented-item {
    flex: 0 0 auto !important;
    min-width: min-content;
  }
  && .ant-segmented-item-label {
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: nowrap;
  }
`

const ItemsScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const EmptyWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  background: #fff;
  border-radius: 10px;
  border: 1px dashed #d9d9d9;
`

const EmptyDesc = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #8c8c8c;
  font-size: 13px;

  strong {
    color: #434343;
    font-size: 14px;
  }
`

const ItemCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #91caff;
    box-shadow: 0 2px 8px rgba(22, 119, 255, 0.08);
  }
`

const ItemIndex = styled.div`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: #e6f4ff;
  color: #1677ff;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ItemThumb = styled.div`
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  background: #fafafa;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
`

const ItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`

const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #141414;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const ItemMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 8px;
`

const QtyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid #f5f5f5;
`

const QtyLabel = styled.span`
  font-size: 12px;
  color: #595959;
`

const SpinWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
`
